import type { ChipProps } from '@packages/shared';
import { Avatar, Box, Chip, Rating } from '@packages/shared';
import type { FC } from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import { Fragment } from 'react';
import type { SelectedFilter } from '../../api/rest/types';
import { useFilters } from '../../hooks/useFilters';

const deleteAllLabel = defineMessage({
  id: 'filters.selectedValues.deleteAll',
  defaultMessage: 'Alle Filter zurücksetzen',
});

const SelectedValueChip = ({
  filters,
  filterId,
  valueId,
  showColors,
  onDelete,
}: {
  filters: SelectedFilter[];
  filterId: string;
  valueId: string;
  showColors?: boolean;
} & Pick<ChipProps, 'onDelete'>) => {
  const filter = filters.find((x) => x.id === filterId);

  const value = filter?.groupedValues.flatMap((x) => x.values).find((x) => x.id === valueId);

  const label = value?.name;
  const rating = value && 'rating' in value ? value.rating : undefined;

  const icon = rating ? <Rating value={rating} readOnly sx={{ paddingLeft: 0.25 }} /> : undefined;
  const avatar =
    showColors && value && 'image' in value && 'url' in value.image && !('rating' in value) ? (
      <Avatar src={value.image.url} alt={value.image.alt} />
    ) : undefined;

  return <Chip icon={icon} avatar={avatar} label={label} onDelete={onDelete} variant="outlined" />;
};

export type SelectedFilterValueListProps = {
  /** All currently available filters for the search result or category page */
  selectedFilters: SelectedFilter[];
  /** If `true`, the color of the filter value is displayed on the chip */
  showColors?: boolean;
};

/**
 * A list of currently selected filter values
 * */
export const SelectedFilterValueList: FC<SelectedFilterValueListProps> = ({
  selectedFilters,
  showColors,
}) => {
  const { selectedFilterValues, pushFilterChange } = useFilters(selectedFilters);

  const removeFilterValue = (filterId: string, valueId: string) => {
    const otherValues = selectedFilterValues.filter((value) => value.filterId !== filterId);
    const relevantValue = selectedFilterValues.find((value) => value.filterId === filterId);

    if (!relevantValue) return;

    const { selectedValues, selectedRange } = relevantValue;

    pushFilterChange(
      otherValues.concat({
        filterId,
        selectedRange,
        selectedValues: selectedValues.filter((id) => id !== valueId),
      }),
    );
  };

  const removeRangeValue = (filterId: string) => {
    const otherValues = selectedFilterValues.filter((value) => value.filterId !== filterId);
    const relevantValue = selectedFilterValues.find((value) => value.filterId === filterId);

    if (!relevantValue) return;

    const { selectedValues } = relevantValue;

    pushFilterChange(otherValues.concat({ filterId, selectedValues }));
  };

  const removeAll = () => {
    pushFilterChange([]);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: {
          xs: 'column',
          md: 'row',
        },
        flexWrap: 'wrap',
        width: '100%',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', flex: 1, flexWrap: 'wrap', gap: 1, marginRight: 'auto' }}>
        {selectedFilterValues.flatMap(({ filterId, selectedValues, selectedRange }) => (
          <Fragment key={filterId}>
            {selectedRange && (
              <Chip
                label={`${selectedRange.range.join('-')} ${selectedRange.unit}`}
                onDelete={() => removeRangeValue(filterId)}
                variant="outlined"
              />
            )}
            {selectedValues.map((id) => (
              <SelectedValueChip
                key={`${filterId}-${id}`}
                onDelete={() => removeFilterValue(filterId, id)}
                filters={selectedFilters}
                filterId={filterId}
                valueId={id}
                showColors={showColors}
              />
            ))}
          </Fragment>
        ))}
      </Box>
      {/* Box wrapper provides left-alignment for the Chip on small device sizes (overrides the default stretch alignment of the parent); this is functionally equivalent to `align-self: flex-start` directly on the chip, but makes it consistent with the other chips above that also have a Box wrapper */}
      {selectedFilterValues.length > 0 && (
        <Box>
          <Chip
            label={<FormattedMessage {...deleteAllLabel} />}
            onDelete={removeAll}
            onClick={removeAll}
          />
        </Box>
      )}
    </Box>
  );
};
